import { attachmentUpload, list } from '@/router/utils'
import { AttachmentTableWithTabs } from '@/shared/components/attachments'
import { attachmentApi } from '@/store/services/attachment.service'
import { Button, Dialog, DialogBody, DialogFooter, DialogHeader } from '@material-tailwind/react'
import { useEffect, useMemo, useState } from 'react'
import {
  Link,
  createSearchParams,
  generatePath,
  useLoaderData,
  useSearchParams
} from 'react-router-dom'
import { useAuthorization } from '@/hooks'
import { store } from '@/store'
import { isAdministradorRole } from '@/utils/user-roles'
import { useSelector } from 'react-redux'

export const AssetV2AttachmentList = () => {
  const { hasAccess } = useAuthorization({
    redirection: { enable: true }
  })
  const currentUser = useSelector((state) => state.user.current)
  const asset = useLoaderData()
  const [searchParams] = useSearchParams()
  const [isNavigatingToUpload, setIsNavigatingToUpload] = useState(false)
  const [showUploadLoadingMessage, setShowUploadLoadingMessage] = useState(false)
  const modalSearchParams = useMemo(() => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('attachment_tab')
    nextParams.delete('attachment_reload')
    return nextParams
  }, [searchParams])
  const currentUrlParams = modalSearchParams.toString()
    ? `?${modalSearchParams.toString()}`
    : ''

  const defaultSearchParams = useMemo(() => {
    const reloadToken = searchParams.get('attachment_reload')

    return {
      attachable_type: 'Asset',
      attachable_id: asset.id,
      ...(reloadToken ? { attachment_reload: reloadToken } : {})
    }
  }, [asset.id, searchParams])
  const canAddDocument = isAdministradorRole(currentUser)

  const getMore = async (searchParams) => {
    const { attachments = [], meta = {} } = await store.dispatch(
      attachmentApi.endpoints.getAttachments.initiate(
        `?${searchParams.toString()}`,
        { forceRefetch: true }
      )
    ).unwrap()

    const currentPage = Number(searchParams.get('page'))
    const hasMore = currentPage < (meta?.pagination?.total_pages || 0)

    return { attachments, hasMore }
  }

  useEffect(() => {
    hasAccess('asset_v2#attachments')
  }, [])

  useEffect(() => {
    if (!isNavigatingToUpload) {
      setShowUploadLoadingMessage(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setShowUploadLoadingMessage(true)
    }, 500)

    return () => window.clearTimeout(timeoutId)
  }, [isNavigatingToUpload])

  const blockWhileNavigating = (event) => {
    if (!isNavigatingToUpload) return

    event.preventDefault()
  }

  return (
    <Dialog size='xl' className='h-4/5' open={true}>
      <DialogHeader>Detalle de documentos subidos</DialogHeader>
      <DialogBody className='relative overflow-hidden' style={{ height: 'inherit' }}>
        {isNavigatingToUpload && (
          <div className='absolute inset-0 z-10 bg-white/55 pointer-events-auto'>
            {showUploadLoadingMessage && (
              <div className='absolute inset-0 flex items-center justify-center'>
                <div className='rounded-lg bg-white px-4 py-3 shadow-md'>Cargando...</div>
              </div>
            )}
          </div>
        )}
        <AttachmentTableWithTabs
          initialSearchParams={defaultSearchParams}
          getMore={getMore}
          initialTab={searchParams.get('attachment_tab')}
          onUploadNavigateStart={() => setIsNavigatingToUpload(true)}
          buildUploadPath={(attachment, currentTab) =>
            generatePath(
              '/assets_v2/:id/attachments/:attachmentId/upload_attachment',
              {
                id: asset.id,
                attachmentId: attachment.id
              }
            ) + `?${createSearchParams({
              ...Object.fromEntries(searchParams.entries()),
              attachment_tab: currentTab
            }).toString()}`
          }
        />
      </DialogBody>
      <DialogFooter className='flex gap-3'>
        <Link to={`${list('assets_v2')}${currentUrlParams}`} onClick={blockWhileNavigating}>
          <Button variant='outlined' color='white' disabled={isNavigatingToUpload}>
            Volver
          </Button>
        </Link>
        {canAddDocument && (
          <Link to={attachmentUpload('assets_v2', asset.id) + currentUrlParams} onClick={blockWhileNavigating}>
            <Button disabled={isNavigatingToUpload}>Añadir documento</Button>
          </Link>
        )}
      </DialogFooter>
    </Dialog>
  )
}
